import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import FilterListIcon from "@mui/icons-material/FilterList";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  TextField,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Autocomplete,
  AppBar,
  Toolbar,
  Paper,
  DialogTitle,
  useTheme,
  useMediaQuery,
  Rating,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { Add, Close, ExpandMore, Remove } from "@mui/icons-material";
import SessionTimer from "./SessionTimer";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import BrokenImageRoundedIcon from "@mui/icons-material/BrokenImageRounded";
import SetCard from "./SetCard";
import { SecureImage } from "./SecureImage";
import ZoomableImage from "./ZoomableImage";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
const ITEMS_PER_PAGE = 30;

const NewStockDataPage = ({
  linkType,
  linkName,
  linkId,
  mfr_id,
  userInfo,
  sessionInfo,
  setSessionInfo,
}) => {
  // ==================== THEME & MEDIA QUERY ====================
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const stockPath = location.pathname;

  // ==================== STATES ====================
  // UI States
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isSet, setIsSet] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [linkNotFound, setLinkNotFound] = useState(false);
  const [emailSuccessOpen, setEmailSuccessOpen] = useState(false);

  // ✅ NEW: Display filter states
  const [showSetsOnly, setShowSetsOnly] = useState(false);
  const [showIndividualsOnly, setShowIndividualsOnly] = useState(false);

  // Stock Data States - ✅ MODIFIED to store both types
  const [setItemsData, setSetItemsData] = useState([]);
  const [individualItemsData, setIndividualItemsData] = useState([]);
  const [lockedStock, setLockedStock] = useState([]);
  const [storeInformation, setStoreInformation] = useState(null);

  // ✅ Pagination states for both types
  const [setItemsPagination, setSetItemsPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    hasNextPage: false,
  });
  const [individualItemsPagination, setIndividualItemsPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    hasNextPage: false,
  });

  // Loading States
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [isFetchingStock, setIsFetchingStock] = useState(false);

  // Pagination & Infinite Scroll States
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Filter States
  const [itemtypes, setItemtypes] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [filters, setFilters] = useState({
    itemType: null,
    design: null,
    grossWtMin: "",
    grossWtMax: "",
    netWtMin: "",
    netWtMax: "",
  });

  // Cart States
  const [cart, setCart] = useState(() => {
    const saved = sessionStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });
  const [cartItemFormData, setCartItemFormData] = useState({});

  // Customer & Order States
  const [customerDetails, setCustomerDetails] = useState({
    name: userInfo?.name || "",
    email: "",
    phone: userInfo?.mobile || "",
    deliveryDate: "",
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isCheckoutLocked, setIsCheckoutLocked] = useState(false);
  const [isExhibitionOrder, setIsExhibitionOrder] = useState(0);
  const [exhibitionName, setExhibitionName] = useState("");

  // PDF States
  const [pdfProgress, setPdfProgress] = useState(0);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfStatus, setPdfStatus] = useState("");

  // Rating State
  const [rating, setRating] = useState(0);

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Loading Messages
  const loadingMessages = [
    "Your order confirmation is on its way...",
    "Uhhh... You've placed quite a remarkable order!...",
    "We're almost done. Just a few seconds left...",
    "Don't worry, we didn't freeze. Just being careful...",
  ];
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(
    loadingMessages[0],
  );

  // ==================== REFS ====================
  const observer = useRef();

  // ==================== EFFECTS ====================
  // Determine if current path is a set view
  // useEffect(() => {
  //   setIsSet(stockPath.includes("/set"));
  // }, [stockPath]);

  // ✅ FIX: Determine if current path is a set view BEFORE fetching
  useEffect(() => {
    const pathIsSet = stockPath.toLowerCase().includes("/set");
    setIsSet(pathIsSet);

    // Now fetch data after we know if it's a set link
    setCurrentPage(1);
    setHasMore(true);
    setSetItemsData([]);
    setIndividualItemsData([]);
    fetchStockData(1, false, activeFilters);
  }, [stockPath, linkId]);

  // Persist cart to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Initial data fetch on component mount or linkId change
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setSetItemsData([]);
    setIndividualItemsData([]);
    fetchStockData(1, false, activeFilters);
  }, [linkId]);

  // Fetch more data when currentPage changes (infinite scroll)
  useEffect(() => {
    if (currentPage > 1) {
      fetchStockData(currentPage, true, activeFilters);
    }
  }, [currentPage]);

  // Rotate loading messages during email sending
  useEffect(() => {
    let intervalId;
    if (isSendingEmail) {
      let index = 0;
      setCurrentLoadingMessage(loadingMessages[0]);
      intervalId = setInterval(() => {
        index += 1;
        if (index < loadingMessages.length) {
          setCurrentLoadingMessage(loadingMessages[index]);
        } else {
          clearInterval(intervalId);
        }
      }, 5000);
    }
    return () => clearInterval(intervalId);
  }, [isSendingEmail]);

  // Session management and user activity tracking
  useEffect(() => {
    if (!sessionInfo?.session_id) return;

    let userOffline = false;
    let pingInterval = null;
    let afkTimeout = null;
    let lockedStockCheckerInterval = null;
    let lastRefreshTime = 0;

    const markUserOffline = async () => {
      if (userOffline) return;
      userOffline = true;
      try {
        await releaseAllCartItems();
        await axios.post(
          `${API_BASE_URL}/api/auth/link/session/offline`,
          { session_id: sessionInfo.session_id },
          { headers: { "Content-Type": "application/json" } },
        );
      } catch (error) {
        console.error("Failed to mark user offline:", error);
      } finally {
        clearInterval(pingInterval);
        clearInterval(lockedStockCheckerInterval);
        clearTimeout(afkTimeout);
        removeActivityListeners();
      }
    };

    const pingServer = async () => {
      if (userOffline) return;
      try {
        await axios.post(`${API_BASE_URL}/api/auth/link/session/ping`, {
          session_id: sessionInfo.session_id,
        });
      } catch (error) {
        console.error("Ping error:", error);
      }
    };

    const refreshSession = async () => {
      if (userOffline) return;
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/auth/link/session/refresh`,
          { session_id: sessionInfo.session_id, extendSessionMin: 60 },
        );
        if (res.data?.expires_at) {
          setSessionInfo((prev) => ({
            ...prev,
            expires_at: res.data.expires_at,
          }));
        }
      } catch (error) {
        console.error("Refresh error:", error);
      }
    };

    const updateActivity = () => {
      if (userOffline) return;
      const now = Date.now();
      if (now - lastRefreshTime > 1000) {
        refreshSession();
        lastRefreshTime = now;
      }
      clearTimeout(afkTimeout);
      afkTimeout = setTimeout(markUserOffline, 15 * 60 * 1000);
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "touchmove",
    ];

    const addActivityListeners = () => {
      activityEvents.forEach((event) =>
        window.addEventListener(event, updateActivity, { passive: true }),
      );
    };

    const removeActivityListeners = () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, updateActivity),
      );
    };

    const lockedStockChecker = async () => {
      if (userOffline) return;
      try {
        const url = `${API_BASE_URL}/api/auth/stock/active?store_id=${mfr_id}&linkid=${linkId}`;
        const res = await axios.get(url);
        if (!res.data?.success) throw res.data;
        const filtered = (res.data?.data || []).filter(
          (item) => item.user_id != userInfo.id,
        );
        setLockedStock(filtered);
      } catch (error) {
        console.error(error);
      }
    };

    addActivityListeners();
    refreshSession();
    lockedStockChecker();
    pingInterval = setInterval(pingServer, 5000);
    lockedStockCheckerInterval = setInterval(lockedStockChecker, 15000);
    afkTimeout = setTimeout(markUserOffline, 15 * 60 * 1000);

    return () => {
      removeActivityListeners();
      clearInterval(pingInterval);
      clearInterval(lockedStockCheckerInterval);
      clearTimeout(afkTimeout);
    };
  }, [sessionInfo?.session_id]);

  // ==================== INTERSECTION OBSERVER (INFINITE SCROLL) ====================
  const lastStockElementRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore],
  );

  // ==================== API FUNCTIONS ====================
  // ✅ MODIFIED: Fetch stock data with both sets and individual items (only for set links)
  const fetchStockData = async (
    page = 1,
    shouldAppend = true,
    filters = {},
  ) => {
    try {
      // FIX: Only set isFetchingStock for filter operations, not pagination
      if (!shouldAppend && page === 1) {
        setIsFetchingStock(true); // Only for filters/initial load
      }

      if (!hasLoadedOnce && page === 1) {
        setLoading(true);
      } else if (page > 1) {
        setLoadingMore(true);
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/auth/link/fetch-stock`,
        {
          linkId,
          mfr_id,
          page,
          limit: ITEMS_PER_PAGE,
          filters,
        },
      );

      if (!response.data?.success) {
        setLinkNotFound(true);
        return;
      }

      // ✅ Check if response has separate setItems and individualItems (set link)
      const hasSetItemsStructure =
        response.data?.setItems && response.data?.individualItems;

      if (hasSetItemsStructure) {
        // This is a SET LINK - extract both types
        const fetchedSetItems = response.data?.setItems?.data || [];
        const fetchedIndividualItems =
          response.data?.individualItems?.data || [];

        // Update set items with append/replace logic
        if (shouldAppend && page > 1) {
          setSetItemsData((prev) => [...prev, ...fetchedSetItems]);
          setIndividualItemsData((prev) => [
            ...prev,
            ...fetchedIndividualItems,
          ]);
        } else {
          setSetItemsData(fetchedSetItems);
          setIndividualItemsData(fetchedIndividualItems);
        }

        // Update pagination info for both types
        setSetItemsPagination({
          currentPage: page,
          totalPages: response.data?.setItems?.totalPages ?? 0,
          totalCount: response.data?.setItems?.totalCount ?? 0,
          hasNextPage:
            response.data?.setItems?.pagination?.hasNextPage ?? false,
        });

        setIndividualItemsPagination({
          currentPage: page,
          totalPages: response.data?.individualItems?.totalPages ?? 0,
          totalCount: response.data?.individualItems?.totalCount ?? 0,
          hasNextPage:
            response.data?.individualItems?.pagination?.hasNextPage ?? false,
        });

        // Update hasMore based on EITHER type having more pages
        setHasMore(
          response.data?.setItems?.pagination?.hasNextPage ||
            response.data?.individualItems?.pagination?.hasNextPage ||
            false,
        );
      } else {
        // This is a REGULAR LINK - only individual items
        const fetchedStock = response.data?.data || [];

        if (shouldAppend && page > 1) {
          setIndividualItemsData((prev) => [...prev, ...fetchedStock]);
        } else {
          setIndividualItemsData(fetchedStock);
        }

        // Clear set items data for non-set links
        setSetItemsData([]);

        // Update pagination for individual items only
        setIndividualItemsPagination({
          currentPage: page,
          totalPages: response.data?.totalPages ?? 0,
          totalCount: response.data?.totalCount ?? 0,
          hasNextPage: response.data?.pagination?.hasNextPage ?? false,
        });

        setHasMore(response.data?.pagination?.hasNextPage ?? false);
      }

      setStoreInformation(response?.data?.store_info);
      setIsExhibitionOrder(response.data?.isExhibitionOrder ?? 0);
      setExhibitionName(response.data?.exhibitionName ?? "");
      setItemtypes(response.data?.availableItemTypes || []);
      setDesigns(response.data?.availableDesignTypes || []);

      // Auto-add items to cart for exhibition orders
      if (response.data?.isExhibitionOrder === 1) {
        setCart((prev) => {
          const existing = new Set(prev.map((i) => i.tagno));
          let allItems = [];

          if (hasSetItemsStructure) {
            const setItemsFlat = response.data.setItems.data.flatMap((set) => [
              set.parentItem,
              ...set.subItems,
            ]);
            allItems = [...setItemsFlat, ...response.data.individualItems.data];
          } else {
            allItems = response.data.data;
          }

          return [...prev, ...allItems.filter((i) => !existing.has(i.tagno))];
        });
      }

      setHasLoadedOnce(true);
    } catch (e) {
      console.error("Fetch stock error:", e);
      setError("Failed to load stock data.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setIsFetchingStock(false);
    }
  };

  // Log user actions for analytics
  const logUserAction = async (tag_no, action_type) => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/activity/log`, {
        user_id: userInfo?.id || userInfo?.user_id,
        store_id: Number(mfr_id),
        linkid: linkId,
        action_type,
        tag_no,
      });
    } catch (error) {
      console.error("Failed to log user activity:", error);
    }
  };

  // Lock stock item(s) when added to cart
  const lockStock = async (tagno) => {
    try {
      const payload = {
        user_id: userInfo?.id,
        store_id: Number(mfr_id),
        linkid: linkId,
        tag_no: Array.isArray(tagno) ? tagno : tagno,
        expires_at: formatDate(sessionInfo?.expires_at),
      };
      const res = await axios.post(
        `${API_BASE_URL}/api/auth/stock/lock`,
        payload,
      );
      if (!res.data?.success) throw res.data;
    } catch (error) {
      console.error("Lock stock failed:", error);
      throw error;
    }
  };

  // Release all cart items when user goes offline
  const releaseAllCartItems = async () => {
    try {
      if (cart.length === 0) return;

      const allTagNumbers = cart.map((item) => item.tagno);
      const releasePromises = allTagNumbers.map(async (tagno) => {
        try {
          const params = { tag_no: tagno, reason: "user_offline" };
          const res = await axios.post(
            `${API_BASE_URL}/api/auth/stock/release`,
            params,
          );
          if (!res.data?.success) {
            console.error(`Failed to release tag ${tagno}:`, res.data);
          }
          logUserAction(tagno, "RELEASE_OFFLINE");
        } catch (error) {
          console.error(`Error releasing tag ${tagno}:`, error);
        }
      });

      await Promise.allSettled(releasePromises);

      setCart([]);
      sessionStorage.removeItem("cart");
      setCartItemFormData({});
      sessionStorage.removeItem("cartItemFormData");
    } catch (error) {
      console.error("Error in releaseAllCartItems:", error);
    }
  };

  // ==================== CART FUNCTIONS ====================
  // Add item to cart
  const addToCart = async (item) => {
    try {
      // Check if this is a set item (has parentItem property)
      const isSetItem = item.hasOwnProperty("parentItem");

      if (!isSetItem) {
        // Individual item
        if (!cart.some((cartItem) => cartItem.tagno === item.tagno)) {
          setCart((prevCart) => {
            const updated = [...prevCart, item];
            sessionStorage.setItem("cart", JSON.stringify(updated));
            return updated;
          });
          await lockStock(item?.tagno);
          logUserAction(item?.tagno, "ADD_CART");
        }
      } else {
        // Set item (parent + children)
        const allItems = [
          { ...item?.parentItem, out_of_stock: item.out_of_stock },
          ...(item?.subItems || []),
        ];
        const itemsToAdd = allItems.filter(
          (cartItem) => !cart.some((c) => c?.tagno === cartItem?.tagno),
        );
        if (itemsToAdd.length > 0) {
          setCart((prevCart) => {
            const updated = [...prevCart, ...itemsToAdd];
            sessionStorage.setItem("cart", JSON.stringify(updated));
            return updated;
          });
          const tagNumbers = itemsToAdd.map((i) => i?.tagno);
          await lockStock(tagNumbers);
          itemsToAdd.forEach((i) => logUserAction(i?.tagno, "ADD_CART"));
        }
      }
    } catch (error) {
      console.error("Add to cart failed:", error);
    }
  };

  // Remove item from cart (handles sets and individual items)
  const removeFromCart = async (stock) => {
    try {
      const clickedTag = stock.tagno;
      const clickedCrosstag = Number(stock.crosstag || 0);
      let tagNumbers = [];

      if (clickedCrosstag == 0) {
        const children = cart.filter(
          (item) => Number(item.crosstag) === clickedTag,
        );
        if (children.length > 0) {
          tagNumbers = [clickedTag, ...children.map((i) => i.tagno)];
        } else {
          tagNumbers = [clickedTag];
        }
      } else {
        const parentTag = clickedCrosstag;
        const children = cart.filter(
          (item) => Number(item.crosstag) === parentTag,
        );
        tagNumbers = [parentTag, ...children.map((i) => i.tagno)];
      }

      tagNumbers = [...new Set(tagNumbers)];

      setCart((prev) => {
        const updated = prev.filter((i) => !tagNumbers.includes(i.tagno));
        sessionStorage.setItem("cart", JSON.stringify(updated));
        return updated;
      });

      setCartItemFormData((prev) => {
        const updated = { ...prev };
        tagNumbers.forEach((t) => delete updated[t]);
        sessionStorage.setItem("cartItemFormData", JSON.stringify(updated));
        return updated;
      });

      for (let tag of tagNumbers) {
        const params = { tag_no: tag, reason: "removed_from_cart" };
        const res = await axios.post(
          `${API_BASE_URL}/api/auth/stock/release`,
          params,
        );
        if (!res.data?.success) throw res.data;
        logUserAction(tag, "REMOVE_CART");
      }
    } catch (error) {
      console.error("Remove cart error:", error);
    }
  };

  // Get cart item count (handles sets properly)
  const getCartCount = () => {
    const processedChildren = new Set();
    let count = 0;

    cart.forEach((item) => {
      if (processedChildren.has(item.tagno)) return;

      const isChild = cart.some(
        (p) => Number(item.crosstag) === Number(p.tagno) && item.crosstag !== 0,
      );

      if (isChild) {
        processedChildren.add(item.tagno);
        return;
      }

      const children = cart.filter(
        (child) =>
          Number(child.crosstag) === item.tagno && child.tagno !== item.tagno,
      );
      children.forEach((child) => processedChildren.add(child.tagno));
      count++;
    });

    return count;
  };

  // Calculate total net weight
  const getTotalNetWeight = () => {
    return cart
      .reduce((total, item) => total + parseFloat(item?.netwt || 0), 0)
      .toFixed(3);
  };

  // Calculate total gross weight
  const getTotalGrossWeight = () => {
    return cart
      .reduce((total, item) => total + parseFloat(item?.gross || 0), 0)
      .toFixed(3);
  };

  // Build cart items payload for checkout
  const buildCartItemsPayload = (cart) => {
    const availableItems = { setItems: [], individualItems: [] };
    const outOfStockItems = { setItems: [], individualItems: [] };
    const parentTagnosInCart = new Set(cart.map((item) => item.tagno));
    const processedChildren = new Set();

    cart.forEach((item) => {
      if (processedChildren.has(item.tagno)) return;

      const isChildWithParent =
        item.crosstag &&
        item.crosstag !== 0 &&
        parentTagnosInCart.has(item.crosstag);

      if (isChildWithParent) {
        processedChildren.add(item.tagno);
        return;
      }

      const children = cart.filter(
        (child) => child.tagno !== item.tagno && child.crosstag === item.tagno,
      );

      const isParent = children.length > 0;
      children.forEach((child) => processedChildren.add(child.tagno));

      const parentOutOfStock =
        item?.out_of_stock === true ||
        children.some((c) => c?.out_of_stock === true);

      const isSetItem = isParent && children.length > 0;

      if (parentOutOfStock) {
        if (isSetItem) {
          outOfStockItems.setItems.push(item, ...children);
        } else {
          outOfStockItems.individualItems.push(item);
        }
      } else {
        if (isSetItem) {
          availableItems.setItems.push(item, ...children);
        } else {
          availableItems.individualItems.push(item);
        }
      }
    });

    return { availableItems, outOfStockItems };
  };

  // ==================== FILTER FUNCTIONS ====================
  // Handle filter input changes
  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Check if any filter is active
  const shouldFilterBeEnabled = () => {
    return (
      filters.itemType !== null ||
      filters.design !== null ||
      filters.grossWtMin !== "" ||
      filters.grossWtMax !== "" ||
      filters.netWtMin !== "" ||
      filters.netWtMax !== ""
    );
  };

  // Apply filters (server-side)
  const handleFilter = async () => {
    const serverFilters = {};
    if (filters.itemType) serverFilters.itemType = filters.itemType;
    if (filters.design) serverFilters.design = filters.design;
    if (filters.grossWtMin !== "")
      serverFilters.grossWtMin = parseFloat(filters.grossWtMin);
    if (filters.grossWtMax !== "")
      serverFilters.grossWtMax = parseFloat(filters.grossWtMax);
    if (filters.netWtMin !== "")
      serverFilters.netWtMin = parseFloat(filters.netWtMin);
    if (filters.netWtMax !== "")
      serverFilters.netWtMax = parseFloat(filters.netWtMax);

    setActiveFilters(serverFilters);
    setIsFilterActive(true);
    setCurrentPage(1);
    setSetItemsData([]);
    setIndividualItemsData([]);
    setHasMore(true);
    setIsFilterDrawerOpen(false);

    await fetchStockData(1, false, serverFilters); // false = don't append
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      itemType: null,
      design: null,
      grossWtMin: "",
      grossWtMax: "",
      netWtMin: "",
      netWtMax: "",
    });

    setActiveFilters({});
    setIsFilterActive(false);
    setIsFilterDrawerOpen(false);

    setCurrentPage(1);
    setSetItemsData([]);
    setIndividualItemsData([]);
    setHasMore(true);
    fetchStockData(1, false, {});
  };

  // ✅ NEW: Handle checkbox changes
  const handleShowSetsOnly = (event) => {
    const checked = event.target.checked;
    setShowSetsOnly(checked);
    if (checked) {
      setShowIndividualsOnly(false); // Uncheck the other
    }
  };

  const handleShowIndividualsOnly = (event) => {
    const checked = event.target.checked;
    setShowIndividualsOnly(checked);
    if (checked) {
      setShowSetsOnly(false); // Uncheck the other
    }
  };

  // ==================== CHECKOUT FUNCTIONS ====================
  const handleSendEmail = async () => {
    if (isCheckoutLocked) return;

    if (!customerDetails.name) {
      setSnackbar({
        open: true,
        message: "Please enter customer name",
        severity: "warning",
      });
      return;
    }

    if (customerDetails.phone.length !== 10) {
      setSnackbar({
        open: true,
        message: "Phone number must be 10 digits",
        severity: "warning",
      });
      return;
    }

    if (!customerDetails.deliveryDate) {
      setSnackbar({
        open: true,
        message: "Please select delivery date",
        severity: "warning",
      });
      return;
    }

    try {
      if (
        !customerDetails.name ||
        !customerDetails.phone ||
        !customerDetails.deliveryDate
      ) {
        alert("Name, Mobile Number, and Delivery Date are required!");
        return;
      }

      setIsCheckoutLocked(true);
      setIsSendingEmail(true);

      const payload = {
        linkId: linkId,
        customer: customerDetails,
        cartItems: buildCartItemsPayload(cart),
        isSet: isSet,
        mailid: storeInformation.store_mail,
        store_id: storeInformation.store_id,
        store_name: storeInformation.store_name,
        store_logo: storeInformation.store_logo,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/fetchorder/checkout`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.status === 200) {
        setIsCheckoutOpen(false);
        setIsSendingEmail(false);
        setEmailSuccessOpen(true);
        setIsCheckoutLocked(false);

        const order_no = response?.data?.order_no;

        setTimeout(() => {
          generatePDFWithProgress(payload, order_no)
            .then(async (pdfResponse) => {
              const pdf_blob_url = pdfResponse?.data?.file_url;

              if (pdf_blob_url) {
                const WhatsappCusPayload = {
                  token: "d98jl1d2fzhvaj6v32nk606x",
                  phone: `91${customerDetails?.phone}`,
                  fileUrl: pdf_blob_url,
                  message: `Hi ${customerDetails?.name}, your order has been received! 🙏\nOrder No: ${order_no}`,
                };

                const WhatsappMfrPayload = {
                  token: "d98jl1d2fzhvaj6v32nk606x",
                  phone: `919945466600`,
                  fileUrl: pdf_blob_url,
                  message: `New Order Alert 🚨\nOrder No: ${order_no}\nCustomer: ${customerDetails?.name}`,
                };

                const whatsappApiUrl = `${API_BASE_URL}/api/auth/sendwhatsppfile`;

                Promise.allSettled([
                  axios.post(whatsappApiUrl, WhatsappCusPayload),
                  axios.post(whatsappApiUrl, WhatsappMfrPayload),
                  axios.post(
                    `${API_BASE_URL}/api/fetchorder/send-order-mail-with-pdf`,
                    {
                      order_no: order_no,
                      pdf_url: pdf_blob_url,
                      customer: customerDetails,
                      deliveryDate: customerDetails.deliveryDate,
                      store_name: storeInformation.store_name,
                      mailid: storeInformation.store_mail,
                    },
                  ),
                ]).then((results) => {
                  // results.forEach((res, i) => {
                  //   if (res.status === "fulfilled") {
                  //     console.log(
                  //       i === 0
                  //         ? "✅ Customer WhatsApp sent!"
                  //         : i === 1
                  //           ? "✅ Manufacturer WhatsApp sent!"
                  //           : "✅ Email with PDF sent!",
                  //     );
                  //   } else {
                  //     console.error(
                  //       i === 0
                  //         ? "❌ Customer WhatsApp failed"
                  //         : i === 1
                  //           ? "❌ Manufacturer WhatsApp failed"
                  //           : "❌ Email with PDF failed",
                  //       res.reason,
                  //     );
                  //   }
                  // });
                  setIsPdfGenerating(false);
                  setPdfProgress(0);
                });
              } else {
                setIsPdfGenerating(false);
                console.error("PDF URL not received");
              }
            })
            .catch((error) => {
              console.error("PDF generation failed:", error);
              setIsPdfGenerating(false);
              setPdfProgress(0);
            });
        }, 500);

        setCart([]);
        sessionStorage.removeItem("cart");
        setCartItemFormData({});
        sessionStorage.removeItem("cartItemFormData");
      }
    } catch (error) {
      console.error("Error in checkout:", error);
      alert(
        error.response?.data?.error ||
          "Failed to process order. Please try again.",
      );
      setIsCheckoutLocked(false);
    } finally {
      setIsSendingEmail(false);
      setIsCheckoutLocked(false);
    }
  };

  // ==================== PDF FUNCTIONS ====================
  const buildSecureImageUrl = (item) => {
    const params = new URLSearchParams({
      encryptedUrl: item.imageUrl,
      imageKey: item.imageKey,
      iv: item.iv,
      authTag: item.authTag,
    });
    return `${API_BASE_URL}/api/fetchorder/secured-image?${params.toString()}`;
  };

  async function fetchImageAsBase64(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Image fetch failed");
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        compressImage(reader.result, 0.6, 300).then(resolve).catch(reject);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  function compressImage(base64, quality = 0.6, maxWidth = 300) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
        resolve(compressedBase64);
      };
      img.onerror = () => reject(new Error("Image compression failed"));
      img.src = base64;
    });
  }

  async function attachPdfImage(item) {
    if (!item?.imageUrl || !item?.imageKey) return { ...item, pdfImage: null };
    try {
      const secureUrl = buildSecureImageUrl(item);
      const pdfImage = await fetchImageAsBase64(secureUrl);
      return { ...item, pdfImage };
    } catch (e) {
      console.warn("Failed to load image for tag:", item?.tagno);
      return { ...item, pdfImage: null };
    }
  }

  async function prepareItemsForPdf(items) {
    const BATCH_SIZE = 10;
    const enriched = [];
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = items.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map((item) => attachPdfImage(item)),
      );
      enriched.push(...batchResults);
    }
    return enriched;
  }

  const generatePDF = async (orderData, order_no) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const { customer, cartItems, store_name, store_logo } = orderData;

    let items = [];
    if (cartItems?.availableItems?.individualItems) {
      items = [...cartItems.availableItems.individualItems];
    }
    if (cartItems?.availableItems?.setItems) {
      items = [...items, ...cartItems.availableItems.setItems];
    }
    if (cartItems?.outOfStockItems?.individualItems) {
      items = [
        ...items,
        ...cartItems.outOfStockItems.individualItems.map((i) => ({
          ...i,
          out_of_stock: true,
        })),
      ];
    }
    if (cartItems?.outOfStockItems?.setItems) {
      items = [
        ...items,
        ...cartItems.outOfStockItems.setItems.map((i) => ({
          ...i,
          out_of_stock: true,
        })),
      ];
    }

    items = await prepareItemsForPdf(items);

    const sets = new Map();
    const individualItems = [];
    const existingTagNos = new Set(items.map((item) => item.tagno));

    items.forEach((item) => {
      const crossTag = item.crosstag || 0;
      if (crossTag !== 0) {
        if (existingTagNos.has(crossTag)) {
          if (!sets.has(crossTag)) {
            sets.set(crossTag, { parent: null, children: [] });
          }
          sets.get(crossTag).children.push(item);
        } else {
          individualItems.push(item);
        }
      }
    });

    items.forEach((item) => {
      const crossTag = item.crosstag || 0;
      if (crossTag === 0) {
        const hasChildren = items.some((i) => (i.crosstag || 0) === item.tagno);
        if (hasChildren) {
          if (!sets.has(item.tagno)) {
            sets.set(item.tagno, { parent: item, children: [] });
          } else {
            sets.get(item.tagno).parent = item;
          }
        } else {
          individualItems.push(item);
        }
      }
    });

    const headerHeight = 30;
    doc.setFillColor(41, 58, 75);
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    if (store_logo) {
      try {
        doc.addImage(store_logo, "JPEG", 10, 5, 20, 20, undefined, "MEDIUM");
      } catch (e) {
        console.warn("Store logo failed to load");
      }
    }

    doc.setTextColor(255);
    doc.setFontSize(18);
    doc.text(store_name || "Store Name", 40, 18);

    if (order_no) {
      const orderText = `Order No: ${order_no}`;
      doc.setFillColor(255, 255, 255);
      doc.rect(pageWidth - 55, 10, 45, 10, "F");
      doc.setTextColor(0);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(orderText, pageWidth - 53, 16);
    }

    const custTableY = headerHeight + 5;
    doc.autoTable({
      startY: custTableY,
      theme: "grid",
      tableWidth: pageWidth - 20,
      margin: { left: 10, right: 10 },
      head: [["Customer Detail", "Value"]],
      body: [
        ["Customer Name", customer.name || "-"],
        ["Phone", customer.phone || "-"],
        ["Email", customer.email || "-"],
        [
          "Delivery Date",
          customer.deliveryDate
            ? new Date(customer.deliveryDate).toLocaleDateString("en-GB")
            : "-",
        ],
      ],
    });

    let currentY = doc.lastAutoTable.finalY + 10;

    const renderTable = (title, dataset) => {
      if (currentY > pageHeight - 60) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.setTextColor(0);
      doc.text(title, 10, currentY);
      currentY += 5;

      const tableRows = dataset.map((item) => [
        "",
        item.tagno_alpha || item.tagno,
        item.itemtype_name || "-",
        item.design_name || "-",
        (item.gross ?? 0).toFixed(3),
        (item.netwt ?? 0).toFixed(3),
        item.out_of_stock ? "Out of Stock" : "Available",
      ]);

      doc.autoTable({
        startY: currentY,
        head: [
          [
            "Image",
            "Tag No",
            "Item Type",
            "Design",
            "Gross Wt (g)",
            "Net Wt (g)",
            "Status",
          ],
        ],
        body: tableRows,
        columnStyles: {
          0: { cellWidth: 20, halign: "center" },
          1: { cellWidth: 25 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
          6: { cellWidth: 25 },
        },
        bodyStyles: {
          minCellHeight: 20,
          fontSize: 9,
        },
        headStyles: {
          fontSize: 9,
          fillColor: [41, 58, 75],
        },
        margin: { left: 10, right: 10 },
        didDrawCell: (data) => {
          if (data.section === "body" && data.column.index === 0) {
            const img = dataset[data.row.index]?.pdfImage;
            if (!img) return;
            const w = data.cell.width - 4;
            const h = data.cell.height - 4;
            try {
              doc.addImage(
                img,
                "JPEG",
                data.cell.x + 2,
                data.cell.y + 2,
                w,
                h,
                undefined,
                "MEDIUM",
              );
            } catch (e) {
              console.warn(
                "Failed to add image for tag:",
                dataset[data.row.index]?.tagno,
              );
            }
          }
        },
        didDrawPage: (data) => {
          currentY = data.cursor.y;
        },
      });

      currentY = doc.lastAutoTable.finalY + 10;
    };

    if (individualItems.length)
      renderTable("Individual Items", individualItems);

    if (sets.size > 0) {
      sets.forEach(({ parent, children }) => {
        if (!parent) return;
        renderTable(`${parent.itemtype_name} Set`, [parent, ...children]);
      });
    }

    if (currentY > pageHeight - 30) {
      doc.addPage();
      currentY = 20;
    }

    const totalGross = items
      .reduce((sum, i) => sum + (i.gross || 0), 0)
      .toFixed(3);
    const totalNet = items
      .reduce((sum, i) => sum + (i.netwt || 0), 0)
      .toFixed(3);

    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(`Total Gross Weight: ${totalGross} g`, 10, currentY);
    doc.text(`Total Net Weight: ${totalNet} g`, 10, currentY + 6);
    doc.text(`Total Items: ${items.length}`, 10, currentY + 12);

    const filename = `${customer.name || "order"}_order.pdf`;
    doc.save(filename);

    const pdfBlob = doc.output("blob");
    const pdfFile = new File([pdfBlob], filename, {
      type: "application/pdf",
    });

    const formData = new FormData();
    formData.append("pdf", pdfFile);

    const uploadResponse = await fetch(`${API_BASE_URL}/api/auth/uploadpdf`, {
      method: "POST",
      body: formData,
    });

    return await uploadResponse.json();
  };

  const generatePDFWithProgress = async (orderData, order_no) => {
    setIsPdfGenerating(true);
    setPdfProgress(0);
    setPdfStatus("Starting PDF generation...");
    try {
      const result = await generatePDF(orderData, order_no);
      setPdfStatus("PDF generated successfully!");
      return result;
    } catch (error) {
      setPdfStatus("PDF generation failed");
      throw error;
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleRatingChange = (event, newValue) => {
    setRating(newValue);
  };

  // ==================== COMPUTED VALUES ====================
  // ✅ MODIFIED: Create display data based on checkbox filters
  const displayStock = useMemo(() => {
    const lockedTags = new Set(lockedStock?.map((item) => Number(item.tag_no)));

    // Helper to mark items as locked
    const markAsLocked = (items, isSetItems = false) => {
      return items.map((item) => {
        let isLocked = false;
        if (isSetItems) {
          const parentTag = item?.parentItem?.tagno;
          const subItemTags = item?.subItems?.map((s) => s.tagno) || [];
          isLocked =
            (parentTag && lockedTags.has(parentTag)) ||
            subItemTags.some((tag) => lockedTags.has(tag));
        } else {
          isLocked = lockedTags.has(item.tagno);
        }
        return { ...item, out_of_stock: isLocked };
      });
    };

    const markedSetItems = markAsLocked(setItemsData, true);
    const markedIndividualItems = markAsLocked(individualItemsData, false);

    // ✅ Apply checkbox filters (only relevant if we have set items)
    const hasSetItems = setItemsData.length > 0;

    if (hasSetItems) {
      // This is a SET LINK - show ONLY set items.
      // NOTE (client request): For a set link we now display set items only.
      // The original logic (below) showed both sets + individual items and
      // allowed toggling between them via the "Show Sets" / "Show Individual
      // Items" checkboxes. It is intentionally COMMENTED OUT (not deleted) so
      // it can be restored if the client later wants individual items shown too.
      return markedSetItems;

      // ---- ORIGINAL LOGIC (kept for easy revert) ----
      // if (showSetsOnly) {
      //   return markedSetItems;
      // } else if (showIndividualsOnly) {
      //   return markedIndividualItems;
      // } else {
      //   // Show all items (default)
      //   return [...markedSetItems, ...markedIndividualItems];
      // }
    } else {
      // This is a REGULAR LINK - only show individual items
      return markedIndividualItems;
    }
  }, [
    setItemsData,
    individualItemsData,
    lockedStock,
    showSetsOnly,
    showIndividualsOnly,
  ]);

  // ✅ Determine if we're showing sets based on display data
  const isDisplayingSetItems = useMemo(() => {
    if (showIndividualsOnly) return false;
    if (showSetsOnly) return true;
    // If showing all, check if first item has parentItem property
    return (
      displayStock.length > 0 && displayStock[0].hasOwnProperty("parentItem")
    );
  }, [displayStock, showSetsOnly, showIndividualsOnly]);

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <CircularProgress size={28} sx={{ mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Loading stock data...
        </Typography>
      </Box>
    );
  }

  // ==================== RENDER ====================
  return (
    <Box>
      {linkNotFound && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: { md: 24, sm: 20, xs: 16 },
              color: "#c6c6c6",
              textAlign: "center",
            }}
          >
            Link Not Found
          </Typography>
        </Box>
      )}

      {!linkNotFound && (
        <>
          {/* Header AppBar */}
          <AppBar
            position="sticky"
            sx={{
              top: 0,
              backgroundColor: "#fff",
              color: "#000",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              backdropFilter: "blur(6px)",
              px: isMobile ? 2 : 5,
              zIndex: (theme) => theme.zIndex.appBar,
            }}
          >
            <Toolbar
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: isMobile ? 60 : 70,
                py: 1,
                gap: 1,
              }}
            >
              {/* Store Logo */}
              <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
                <Box
                  height={isMobile ? 45 : 60}
                  width={isMobile ? 45 : 60}
                  sx={{
                    backgroundImage: `url(${storeInformation?.store_logo})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: "50%",
                    backgroundColor: "#f5f5f5",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <img
                    style={{
                      height: isMobile ? "60px" : "90px",
                      marginLeft: "8px",
                    }}
                    src={storeInformation?.store_logo}
                    alt="logo"
                  />
                </Box>

                {!isMobile && (
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: "#000000ff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {storeInformation?.store_name}
                  </Typography>
                )}
              </Box>

              {/* Link Name */}
              <Typography
                sx={{
                  fontSize: isMobile ? 18 : 28,
                  fontWeight: 600,
                  textAlign: "center",
                  flex: 1,
                  px: 1,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.2,
                }}
              >
                {linkName?.replace(/-/g, " ")}
              </Typography>

              {/* Cart Icon */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {!isMobile && (
                  <Box>
                    <Typography
                      sx={{ fontSize: 14, textAlign: "end", width: "100%" }}
                    >
                      Hey, {userInfo?.name}
                    </Typography>
                    <SessionTimer sessionInfo={sessionInfo} />
                  </Box>
                )}

                <IconButton onClick={() => setIsCartOpen(true)} size="large">
                  <Badge badgeContent={getCartCount()} color="warning">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Filter Button */}
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            px={1}
            sx={{
              position: "sticky",
              top: isMobile ? 60 : 70,
              zIndex: (theme) => theme.zIndex.appBar - 1,
              backgroundColor: "#fff",
              py: 1,
              borderBottom: "1px solid #eee",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              backdropFilter: "blur(6px)",
            }}
          >
            <Button
              variant="contained"
              startIcon={<FilterListIcon />}
              onClick={() => setIsFilterDrawerOpen(true)}
              sx={{
                textTransform: "none",
                bgcolor: "#ba3800ff",
                "&:hover": { bgcolor: "#a33100" },
              }}
            >
              Filters
            </Button>
            {linkType === "private" && isMobile && (
              <Box>
                <Typography
                  sx={{ fontSize: 14, textAlign: "end", width: "100%" }}
                >
                  Hey, {userInfo?.name}
                </Typography>
                <SessionTimer sessionInfo={sessionInfo} />
              </Box>
            )}
          </Box>

          {/* Filter Drawer */}
          <Drawer
            anchor="left"
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            PaperProps={{
              sx: {
                width: "100%",
                maxWidth: 380,
                bgcolor: "#fff",
                borderRadius: "0px 12px 12px 0px",
              },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: "2px solid #ffedd5",
                bgcolor: "#fff7ed",
              }}
            >
              {/* Heading */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#ba3800" }}
              >
                Filter Stocks
              </Typography>

              {/* Cross Icon */}
              <IconButton onClick={() => setIsFilterDrawerOpen(false)}>
                <Close sx={{ color: "#ba3800" }} />
              </IconButton>
            </Box>

            {/* Filter Options */}
            <Box
              sx={{ p: 2.5, overflowY: "auto", height: "calc(100vh - 140px)" }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {/* Show Sets / Individual Items Only Checkboxes */}
                {/* DISABLED (client request): set links now show set items only,
                    so these set/individual toggles are hidden. Change `false` back
                    to `setItemsData.length > 0` to restore the toggles. */}
                {false && setItemsData.length > 0 && (
                  <Box sx={{ width: "100%" }}>
                    {/* Show Sets Only */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showSetsOnly}
                          onChange={handleShowSetsOnly}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#ba3800",
                          }}
                        >
                          Show Sets
                        </Typography>
                      }
                    />

                    {/* Show Individual Items Only */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showIndividualsOnly}
                          onChange={handleShowIndividualsOnly}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#ba3800",
                          }}
                        >
                          Show Individual Items
                        </Typography>
                      }
                    />
                  </Box>
                )}

                {/* Item & Design Type Section */}
                <Box sx={{ width: "100%" }}>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#ba3800",
                      mb: 0.5,
                    }}
                  >
                    Item & Design Type
                  </Typography>

                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={itemtypes || []}
                    getOptionLabel={(option) => option?.name || ""}
                    value={
                      filters.itemType
                        ? itemtypes.find((t) => t.id === filters.itemType) ||
                          null
                        : null
                    }
                    onChange={(e, value) => {
                      setFilters((prev) => ({
                        ...prev,
                        itemType: value?.id || null,
                        design: null,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item Type"
                        size="small"
                        fullWidth
                      />
                    )}
                    sx={{ mb: 1.5 }}
                  />

                  <Autocomplete
                    fullWidth
                    disablePortal
                    options={
                      filters.itemType
                        ? designs.filter(
                            (d) => d.itemtype_id === filters.itemType,
                          )
                        : designs || []
                    }
                    getOptionLabel={(option) => option?.name || ""}
                    value={
                      filters.design
                        ? designs.find((d) => d.id === filters.design) || null
                        : null
                    }
                    onChange={(e, value) => {
                      setFilters((prev) => ({
                        ...prev,
                        design: value?.id || null,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Design"
                        size="small"
                        fullWidth
                      />
                    )}
                    disabled={!filters.itemType}
                  />
                </Box>

                {/* Gross Weight Filter */}
                <Box sx={{ width: "100%" }}>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#ba3800",
                      mb: 0.5,
                    }}
                  >
                    Gross Weight (g)
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <TextField
                      type="number"
                      name="grossWtMin"
                      placeholder="Min"
                      size="small"
                      value={filters.grossWtMin}
                      onChange={handleChange}
                    />
                    <TextField
                      type="number"
                      name="grossWtMax"
                      placeholder="Max"
                      size="small"
                      value={filters.grossWtMax}
                      onChange={handleChange}
                    />
                  </Box>
                </Box>

                {/* Net Weight Filter */}
                <Box sx={{ width: "100%" }}>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#ba3800",
                      mb: 0.5,
                    }}
                  >
                    Net Weight (g)
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    <TextField
                      type="number"
                      name="netWtMin"
                      size="small"
                      placeholder="Min"
                      value={filters.netWtMin}
                      onChange={handleChange}
                    />
                    <TextField
                      type="number"
                      name="netWtMax"
                      size="small"
                      placeholder="Max"
                      value={filters.netWtMax}
                      onChange={handleChange}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Apply / Cancel Filter Buttons */}
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #e5e7eb",
                bgcolor: "#fff",
                position: "absolute",
                bottom: 0,
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    handleFilter();
                    setIsFilterDrawerOpen(false);
                  }}
                  disabled={!shouldFilterBeEnabled()}
                  sx={{
                    bgcolor: "#ba3800",
                    "&:hover": { bgcolor: "#9a2f00" },
                    py: 1.5,
                  }}
                >
                  Apply Filters
                </Button>

                {isFilterActive && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{
                      borderColor: "#ba3800",
                      color: "#ba3800",
                      py: 1.5,
                      "&:hover": { bgcolor: "#fff4e6" },
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </Box>
          </Drawer>

          {/* Stock Grid */}
          <Box
            display="grid"
            gridTemplateColumns={
              isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(264px, 1fr))"
            }
            justifyContent={isMobile ? "center" : "space-between"}
            gap={isMobile ? 1 : 2}
            sx={{ mt: 1, px: isMobile ? 1 : 4 }}
          >
            {isFetchingStock ? (
              <Box
                sx={{
                  textAlign: "center",
                  gridColumn: "1 / -1",
                  py: 4,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Loading...
                </Typography>
              </Box>
            ) : displayStock.length === 0 && !loading && !isFetchingStock ? (
              <Typography>No Stock Found</Typography>
            ) : (
              displayStock.map((stock, index) => {
                const isLastItem = displayStock.length === index + 1;
                const isSetItem = stock.hasOwnProperty("parentItem");

                // ✅ Render Set Card
                if (isSetItem) {
                  return (
                    <Box
                      key={`${stock?.parentItem?.tagno}-${index}`}
                      ref={isLastItem ? lastStockElementRef : null}
                    >
                      <SetCard
                        cart={cart}
                        removeFromCart={removeFromCart}
                        addToCart={addToCart}
                        stock={stock}
                      />
                    </Box>
                  );
                }

                // ✅ Render Individual Item Card
                return (
                  <Box
                    key={`${stock.tagno}-${index}`}
                    ref={isLastItem ? lastStockElementRef : null}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      mt: 1,
                      position: "relative",
                      opacity: stock.out_of_stock ? 0.6 : 1,
                      boxShadow: "0px 0px 5px rgba(157, 211, 216, 0.5)",
                    }}
                  >
                    {stock.out_of_stock && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 0,
                          bgcolor: "#d32f2f",
                          color: "white",
                          px: 1.4,
                          py: 0.3,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          zIndex: 10,
                          clipPath:
                            "polygon(0 0, 100% 0, calc(100% - 10px) 50%, 100% 100%, 0 100%)",
                        }}
                      >
                        OUT OF STOCK
                      </Box>
                    )}

                    <Box
                      sx={{
                        height: isMobile ? "200px" : "244px",
                        borderRadius: 3,
                        position: "relative",
                        overflow: "hidden",
                        cursor: "pointer",
                        "& img": { transition: "all 0.3s ease" },
                        "&:hover img": { transform: "scale(1.02)" },
                      }}
                      onClick={() => {
                        setSelectedItem(stock);
                        logUserAction(stock.tagno, "LIKED_PRODUCT");
                      }}
                    >
                      {!stock?.imageUrl ? (
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f0f0f0",
                            borderRadius: 3,
                          }}
                        >
                          <BrokenImageRoundedIcon
                            sx={{ fontSize: 60, color: "#9e9e9e" }}
                          />
                        </Box>
                      ) : (
                        <SecureImage
                          item={stock}
                          width="100%"
                          height="100%"
                          borderRadius={2}
                          objectFit="cover"
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        pt: 0.4,
                        height: "86px",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            flexDirection: "column",
                            pl: 1,
                          }}
                        >
                          {stock.itemtype_name && (
                            <>
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{
                                  fontSize: "0.8rem",
                                  color: "#ba3800ff",
                                }}
                              >
                                #{stock.tagno}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "#000000ff",
                                }}
                              >
                                {stock.itemtype_name}
                              </Typography>
                            </>
                          )}

                          {stock.gross && (
                            <>
                              <Typography
                                sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                              >
                                Gross: {parseFloat(stock.gross).toFixed(3)}g
                              </Typography>
                              <Typography
                                sx={{ fontSize: "0.8rem", fontWeight: 500 }}
                              >
                                Net: {parseFloat(stock.netwt).toFixed(3)}g
                              </Typography>
                            </>
                          )}
                        </Box>

                        <Box sx={{ px: 1 }}>
                          {stock.out_of_stock ? (
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: "#d0820cff",
                                color: "white",
                                width: "auto",
                                height: 38,
                                borderRadius: 2,
                                fontSize: "0.7rem",
                                "&:hover": { backgroundColor: "#ac6904ff" },
                              }}
                              onClick={() =>
                                cart.some((item) => item.tagno === stock.tagno)
                                  ? removeFromCart(stock)
                                  : addToCart(stock)
                              }
                            >
                              {cart.some((item) => item.tagno === stock.tagno)
                                ? "Discard Order"
                                : "Place order"}
                            </Button>
                          ) : (
                            <Button
                              variant={
                                cart.some((item) => item.tagno === stock.tagno)
                                  ? ""
                                  : "contained"
                              }
                              color={
                                cart.some((item) => item.tagno === stock.tagno)
                                  ? "#995c00ff"
                                  : undefined
                              }
                              size="small"
                              onClick={() =>
                                cart.some((item) => item.tagno === stock.tagno)
                                  ? removeFromCart(stock)
                                  : addToCart(stock)
                              }
                              sx={{
                                minWidth: 46,
                                width: 46,
                                height: 46,
                                border: `1px solid #995c004e`,
                                borderRadius: "50%",
                                backgroundColor: !cart.some(
                                  (item) => item.tagno === stock.tagno,
                                )
                                  ? "#FFF2DB"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: !cart.some(
                                    (item) => item.tagno === stock.tagno,
                                  )
                                    ? "#ffe0b3"
                                    : "rgba(0,0,0,0.05)",
                                },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 0,
                              }}
                            >
                              {cart.some(
                                (item) => item.tagno === stock.tagno,
                              ) ? (
                                <RemoveShoppingCartIcon
                                  sx={{ fontSize: 20, color: "#a33c00ff" }}
                                />
                              ) : (
                                <AddShoppingCartIcon
                                  sx={{ fontSize: 20, color: "#a33c00ff" }}
                                />
                              )}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          {/* Loading More Indicator */}
          {loadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
                gap: 2,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading more items...
              </Typography>
            </Box>
          )}

          {/* Filter Loading Indicator - Separate */}
          {/* {isFetchingStock && !loadingMore && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "rgba(255, 255, 255, 0.9)",
                zIndex: 9999,
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography>Applying filters...</Typography>
            </Box>
          )} */}

          {/* End of List Message */}
          {!hasMore && displayStock.length > 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                color: "text.secondary",
              }}
            >
              <Typography>You've reached the end of the list</Typography>
            </Box>
          )}

          {/* Cart Drawer */}
          <Drawer
            anchor="right"
            open={isCartOpen}
            onClose={() => setIsCartOpen(false)}
            PaperProps={{
              sx: {
                width: isMobile ? "100%" : 450,
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Header with Close Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="primary"
                  textAlign="center"
                >
                  Cart Items
                </Typography>
                <IconButton onClick={() => setIsCartOpen(false)}>
                  <Close />
                </IconButton>
              </Box>

              {/* Cart Items List */}
              <Box sx={{ flexGrow: 1, overflowY: "auto", mb: 2 }}>
                <List dense>
                  {/* Empty Cart Message */}
                  {cart.length === 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                        Your cart is empty.
                      </Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                        Go ahead and add items.
                      </Typography>
                    </Box>
                  )}

                  {/* Cart Items */}
                  {cart.map((item, index) => {
                    const itemTag = Number(item?.tagno);

                    // Check if this item is a parent (has children)
                    const children = cart.filter(
                      (child) =>
                        Number(child?.crosstag) === itemTag &&
                        child?.tagno !== item?.tagno,
                    );
                    const isParent = children.length > 0;

                    // Check if this item is a child (has a parent in cart)
                    const isChild = cart.some(
                      (p) => Number(item?.crosstag) === Number(p?.tagno),
                    );

                    // Skip rendering children separately
                    if (isChild) return null;

                    // Calculate total weights for sets
                    const totalGross = (
                      parseFloat(item?.gross || 0) +
                      children.reduce(
                        (acc, c) => acc + parseFloat(c?.gross || 0),
                        0,
                      )
                    ).toFixed(3);
                    const totalNet = (
                      parseFloat(item?.netwt || 0) +
                      children.reduce(
                        (acc, c) => acc + parseFloat(c?.netwt || 0),
                        0,
                      )
                    ).toFixed(3);

                    // Check if any item in set is out of stock
                    const outOfStock =
                      item?.out_of_stock === true ||
                      children.some((c) => c?.out_of_stock === true);

                    return (
                      <ListItem
                        key={index}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          px: 1.2,
                          py: 1,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                          bgcolor: "#ffffff",
                          display: "flex",
                          alignItems: "flex-start",
                          width: "100%",
                          minHeight: 90,
                          position: "relative",
                        }}
                      >
                        {/* Out of Stock Label */}
                        {outOfStock && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -6,
                              left: 0,
                              background: "#d32f2f",
                              color: "white",
                              px: 1,
                              py: 0.3,
                              fontSize: "0.65rem",
                              fontWeight: "bold",
                              borderRadius: "0 0 4px 0",
                              zIndex: 10,
                            }}
                          >
                            OUT OF STOCK
                          </Box>
                        )}

                        {/* Item Image */}
                        <Box
                          sx={{
                            width: 70,
                            height: 70,
                            borderRadius: 1,
                            mr: 1.2,
                            flexShrink: 0,
                          }}
                        >
                          <SecureImage
                            item={item}
                            width={70}
                            height={70}
                            borderRadius={1}
                            objectFit="contain"
                            showFallbackIcon={true}
                          />
                        </Box>

                        {/* Item Details and Remove Button */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          {/* Item Info */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.2,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={600}
                              sx={{ fontSize: "0.80rem", lineHeight: 1.1 }}
                            >
                              {isParent
                                ? `${item?.itemtype_name} (Set - ${children.length + 1})`
                                : item?.itemtype_name}{" "}
                              (Tag: {item?.tagno_alpha || item?.tagno})
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.72rem" }}
                            >
                              {isParent ? "Total " : ""} Gross:{" "}
                              {isParent
                                ? totalGross
                                : parseFloat(item?.gross).toFixed(3)}{" "}
                              g
                            </Typography>

                            {storeInformation?.store_id !== 13 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.72rem" }}
                              >
                                {isParent ? "Total " : ""} Net:{" "}
                                {isParent
                                  ? totalNet
                                  : parseFloat(item?.netwt).toFixed(3)}{" "}
                                g
                              </Typography>
                            )}
                          </Box>

                          {/* Remove Button */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "flex-end",
                              alignItems: "flex-end",
                            }}
                          >
                            <Button
                              onClick={() => removeFromCart(item)}
                              color="error"
                              variant="outlined"
                              size="small"
                              sx={{
                                textTransform: "none",
                                fontSize: "0.70rem",
                                height: 26,
                                minWidth: 110,
                                py: 0,
                                mt: "auto",
                              }}
                              startIcon={
                                <DeleteRoundedIcon
                                  sx={{ fontSize: "0.9rem" }}
                                />
                              }
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>

              {/* Cart Totals */}
              {storeInformation?.store_id !== 13 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="flex-start"
                  flexDirection="column"
                  width="100%"
                  mb={2}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="#0D7377"
                    textAlign="center"
                  >
                    <span style={{ color: "black" }}>Total Net Wt:</span>{" "}
                    {getTotalNetWeight()} g
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="#0D7377"
                    textAlign="center"
                  >
                    <span style={{ color: "black" }}>Total Gross Wt:</span>{" "}
                    {getTotalGrossWeight()} g
                  </Typography>
                </Box>
              )}

              {/* Checkout Button */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setIsCheckoutOpen(true)}
                disabled={cart.length === 0}
                sx={{
                  fontWeight: "bold",
                  py: 1.2,
                  borderRadius: 2,
                }}
              >
                Checkout
              </Button>
            </Box>
          </Drawer>

          {/* Item Details Dialog */}
          <Dialog
            open={Boolean(selectedItem)}
            onClose={() => setSelectedItem(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogContent sx={{ position: "relative", textAlign: "center" }}>
              {/* Close Icon */}
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "#696969",
                  bgcolor: "#ffd580",
                  borderRadius: "50%",
                  cursor: "pointer",
                  zIndex: 10,
                }}
                onClick={() => {
                  setSelectedItem(null);
                  setRating(0);
                }}
              >
                <CloseRoundedIcon sx={{ fill: "#5a3c00", fontSize: 22 }} />
              </Box>

              {/* Item Image & Details */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 3,
                }}
              >
                {/* Item Image */}
                <Box sx={{ flex: 3 }}>
                  <ZoomableImage>
                    <SecureImage
                      item={selectedItem}
                      width={"100%"}
                      height={"100%"}
                      borderRadius={2}
                    />
                  </ZoomableImage>
                </Box>

                {/* Item Details */}
                <Box sx={{ flex: 2, textAlign: "left", width: "100%" }}>
                  {/* Row 1: Tag No + Pcs */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: isMobile ? "row" : "column",
                      mb: isMobile ? 0.5 : 0,
                    }}
                  >
                    <Typography sx={{ color: "#333" }}>
                      Tag No:{" "}
                      <b style={{ color: "#222" }}>{selectedItem?.tagno}</b>
                    </Typography>

                    <Typography sx={{ color: "#333" }}>
                      Pcs: <b style={{ color: "#222" }}>{selectedItem?.pcs}</b>
                    </Typography>
                  </Box>

                  {/* Row 2: Gross + Net */}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: isMobile ? "row" : "column",
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ color: "#333" }}>
                      Gross:{" "}
                      <b style={{ color: "#222" }}>
                        {parseFloat(selectedItem?.gross || 0).toFixed(3)}g
                      </b>
                    </Typography>

                    <Typography sx={{ color: "#333" }}>
                      Net:{" "}
                      <b style={{ color: "#222" }}>
                        {parseFloat(selectedItem?.netwt || 0).toFixed(3)}g
                      </b>
                    </Typography>
                  </Box>

                  {/* <Typography sx={{ color: "#333" }}>
                    Category: <b style={{color:"#222"}}>{selectedItem?.category}</b>
                  </Typography> */}

                  {/* Rating */}
                  {/* <Box mt={2}>
                    <Typography variant="subtitle1">Rate this item:</Typography>
                    <Rating
                      name="image-rating"
                      value={rating}
                      onChange={handleRatingChange}
                      size="small"
                    />
                  </Box> */}

                  {/* Add/Remove Cart Button */}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      onClick={() =>
                        cart.some(
                          (item) =>
                            String(item.tagno) === String(selectedItem?.tagno),
                        )
                          ? removeFromCart(selectedItem)
                          : addToCart(selectedItem)
                      }
                      sx={{
                        fontSize: 14,
                        padding: "5px 14px",
                        minWidth: "80px",
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        background: cart.some(
                          (item) =>
                            String(item?.tagno) === String(selectedItem?.tagno),
                        )
                          ? "#ffb3b3"
                          : "#ffd580",
                        color: "#5a3c00",
                        "&:hover": {
                          background: cart.some(
                            (item) =>
                              String(item?.tagno) ===
                              String(selectedItem?.tagno),
                          )
                            ? "#e39a9a"
                            : "#e6c076",
                        },
                      }}
                    >
                      {cart.some(
                        (item) =>
                          String(item?.tagno) === String(selectedItem?.tagno),
                      )
                        ? "Remove from cart"
                        : "Add to cart"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
          </Dialog>

          {/* Checkout Dialog */}
          <Dialog
            open={isCheckoutOpen}
            onClose={() => setIsCheckoutOpen(false)}
          >
            <DialogContent>
              <TextField
                fullWidth
                label="Name"
                margin="dense"
                value={customerDetails?.name || ""}
                onChange={(e) =>
                  setCustomerDetails({
                    ...customerDetails,
                    name: e.target.value,
                  })
                }
                disabled={linkType === "private"}
                required
              />

              <TextField
                fullWidth
                label="Phone"
                margin="dense"
                value={customerDetails.phone}
                onChange={(e) => {
                  const digitsOnly = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 10);
                  setCustomerDetails({
                    ...customerDetails,
                    phone: digitsOnly,
                  });
                }}
                inputProps={{ maxLength: 10 }}
                disabled={linkType === "private"}
                required
              />

              <TextField
                fullWidth
                label="Email"
                margin="dense"
                value={customerDetails.email}
                onChange={(e) =>
                  setCustomerDetails({
                    ...customerDetails,
                    email: e.target.value,
                  })
                }
              />

              <TextField
                fullWidth
                label="Delivery Date"
                type="date"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={customerDetails.deliveryDate}
                onChange={(e) =>
                  setCustomerDetails({
                    ...customerDetails,
                    deliveryDate: e.target.value,
                  })
                }
                required
              />
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setCustomerDetails({
                    name: "",
                    email: "",
                    phone: "",
                    deliveryDate: "",
                  });
                  setIsCheckoutOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendEmail}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>

          {/* Email Sending Loader */}
          <Dialog open={isSendingEmail}>
            <DialogContent
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4,
                gap: 2,
                textAlign: "center",
              }}
            >
              <CircularProgress color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                {currentLoadingMessage}
              </Typography>
            </DialogContent>
          </Dialog>

          {/* Success Dialog */}
          <Dialog
            open={emailSuccessOpen}
            onClose={() => setEmailSuccessOpen(false)}
          >
            <DialogTitle>Order Placed Successfully</DialogTitle>
            <DialogContent>
              <Typography>Your order has been placed successfully</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEmailSuccessOpen(false)} autoFocus>
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={2500}
            onClose={() =>
              setSnackbar({ open: false, message: "", severity: "" })
            }
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity || "info"}
              variant="filled"
              onClose={() =>
                setSnackbar({ open: false, message: "", severity: "" })
              }
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default NewStockDataPage;
